import useGetProjectEstimates from '@/api/queries/project/useGetProjectEstimates';
import { GetProjectHeatLossEstimateQuery } from '@/gql/graphql';
import { AnchorButton, Badge, Card } from '@payaca/ambrosia';
import { FC } from 'react';
import { Link, useParams } from 'react-router-dom';
import HeatLossAssumptionsAlert from '../HeatLossAssumptionsAlert';
import { GridElement } from './GridElement';

type HeatLossEstimate = NonNullable<
  GetProjectHeatLossEstimateQuery['projectById']['heatLossEstimate']
>;

type HeatLossDetailsCardProps = {
  heatLossEstimate: HeatLossEstimate;
};

const ViewEstimatesButton: FC<{ projectId: string }> = (props) => {
  const { projectId } = props;
  const { project } = useGetProjectEstimates(projectId);
  const estimatesCount = project?.estimates.length ?? 0;

  return (
    <div className="flex justify-end">
      <AnchorButton
        as={Link}
        to={`/projects/${projectId}/estimates`}
        variant="white"
        size="sm"
      >
        View estimates
        {estimatesCount > 0 && (
          <Badge variant="white" size="sm" rounded>
            {estimatesCount}
          </Badge>
        )}
      </AnchorButton>
    </div>
  );
};

const HeatLossDetailsCard: FC<HeatLossDetailsCardProps> = (props) => {
  const { heatLossEstimate } = props;
  const { projectId } = useParams<{ projectId: string }>();

  const {
    heatPumpSizeRange,
    minimumCylinderLitres,
    peakHeatLoad,
    method,
    targetIndoorTemperatureC,
  } = heatLossEstimate;

  return (
    <Card>
      <Card.Body className="space-y-4">
        <h3>Heat pump estimate</h3>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-4">
          <GridElement title="Peak heat loss">
            {peakHeatLoad.toFixed(2)}kW
          </GridElement>
          <GridElement title="Heat pump size">
            {heatPumpSizeRange.lowerKw}–{heatPumpSizeRange.upperKw}kW
          </GridElement>
          {minimumCylinderLitres != null && (
            <GridElement title="Minimum cylinder size">
              {minimumCylinderLitres}L
            </GridElement>
          )}
        </div>
        <HeatLossAssumptionsAlert
          method={method}
          targetIndoorTemperatureC={targetIndoorTemperatureC}
        />
        {projectId && <ViewEstimatesButton projectId={projectId} />}
      </Card.Body>
    </Card>
  );
};

export default HeatLossDetailsCard;
